import { PatientService } from 'app/core/services/patient.service';
import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DropdownInputComponent } from 'app/shared/dropdown-input/dropdown-input.component';
import { PatientStateOnTrialOptions } from 'app/core/constants/patient-state-on-trial-options';
import { TrialAutocompleteComponent } from "../../trial-autocomplete/trial-autocomplete.component";
import { PatientTrial, SiteChangePreviewResult } from "../../../core/models/patient-trial.model";
import { ObjectHelper } from "../../../core/helpers/object.helper";
import { TrialAutocomplete } from "../../trial-autocomplete/trial-autocomplete.model";
import { Countries } from "../../../core/constants/countries";
import { LanguageCultureOptions } from "../../../core/constants/language-culture-options";
import { SelectOption } from "../../../core/models/select-option.model";
import { SiteDropdownOption } from "../../site-dropdown/site-dropdown-option.model";
import { ModalComponent } from "../../modal/modal.component";
import { AuthService } from "../../../core/services/auth.service";
import { RoleNames } from "../../../core/constants/role-names";
import { AlertService } from "../../alert/alert.service";

@Component({
  selector: 'app-patient-manage-trials',
  templateUrl: './patient-manage-trials.component.html',
  styleUrls: ['./patient-manage-trials.component.scss']
})
export class PatientManageTrialsComponent implements AfterViewInit {
  @ViewChild('trialAutocomplete') trialAutocomplete: TrialAutocompleteComponent;
  @ViewChild('stateOnTrialSelect') stateOnTrialSelect: DropdownInputComponent;
  @ViewChild('engineSizeSelect') engineSizeSelect: DropdownInputComponent;
  @ViewChild('changeSiteModal') changeSiteModal: ModalComponent;

  @Input('parentForm') parentForm: UntypedFormGroup;
  @Input('index') index: number;
  @Input('patientTrial') patientTrial: PatientTrial;
  @Input('assignedTrialIds') assignedTrialIds: string[] = [];
  @Input('patientName') patientName: string;
  @Input('displayRemoveButton') displayRemoveButton: boolean;

  @Output('trialAdded') trialAdded = new EventEmitter<PatientTrial>();
  @Output('cancelled') cancelled = new EventEmitter<void>();
  @Output('removeFromTrial') removeFromTrial = new EventEmitter<string>();

  added = false;
  changingSite = false;
  optionsVisible = false;
  endingParticipationReasonVisible = false;
  stateOnTrialOptions = PatientStateOnTrialOptions.all();
  engineSizes: { value: string, text: string }[] = [];
  selectedTrialLabel: string;
  selectedSiteLabel: string;
  countries: { value: string, text: string }[] = [];
  languageOptions: SelectOption[] = [];
  changeSitePreview: SiteChangePreviewResult | null = null;
  isLoadingChangeSitePreview = false;
  private suppressSiteChangeCheck = false;

  // The site the user has picked but which is not yet committed. onConfirmChangeSite() is driven by the
  // dialog and receives no argument, so the selection has to be held here between preview and commit.
  private pendingSiteId: string | null = null;
  private pendingSiteName: string | null = null;

  canChangeSite = false;

  constructor(private patientService: PatientService, private authService: AuthService, private alertService: AlertService) {
    this.canChangeSite = this.authService.hasRole(RoleNames.ProjectCoordinatorTravel)
      || this.authService.hasRole(RoleNames.ProjectCoordinatorExpenses)
      || this.authService.hasRole(RoleNames.ProductAdministrator);

    for (const country of Countries.all()) {
      this.countries.push({ value: country.code, text: country.name });
    }

    const languageCultures = LanguageCultureOptions.all();
    languageCultures.forEach(culture => {
      this.languageOptions.push({ value: culture.cultureInfoCode.toLowerCase(), text: culture.language + ' (' + culture.cultureInfoCode.toLowerCase() + ')' });
    });
    this.languageOptions.sort((a, b) => a.text.localeCompare(b.text));
  }

  ngAfterViewInit() {
    if (this.patientTrial !== undefined && this.patientTrial !== null) {
      const text = this.patientTrial.siteName + ' / ' + 'address to be added';

      // A saved enrolment can legitimately arrive with no site: PatientTrial.SiteId is nullable
      // (ApplicationDbContext maps it IsRequired(false)) and 331 live rows have it null. Calling
      // .substring() straight off those nulls threw here, which aborted the rest of this method and left
      // `added` false — so the widget silently re-rendered as the blank "assign a new trial" form and hid a
      // real enrolment instead of showing it. Degrade to an empty site label rather than losing the row.
      this.selectedSiteLabel = this.truncateLabel(this.patientTrial.siteName);
      this.selectedTrialLabel = this.truncateLabel(this.patientTrial.trialLabel);

      this.stateOnTrialSelect.setValue(this.patientTrial.stateOnTrial);

      // engineSizes is filled in by a second pass in the controller, so it is absent entirely on any
      // response built without it.
      (this.patientTrial.engineSizes ?? []).forEach(engineSize => {
        this.engineSizes.push({ value: engineSize, text: engineSize });
      });
      this.engineSizeSelect.setValue(this.patientTrial.engineSize);

      this.setEndingParticipationVisibility(this.patientTrial.stateOnTrial);

      this.added = true;
    } else {
      this.stateOnTrialSelect.setValue("Active");
    }

    this.listenToTrialSelection();
    this.listenToPatientStateSelection();
    this.listenToSiteChanges();
  }

  generateSiteDisplayName(siteNumber: string, siteName: string, siteCountry: string) : string {
    let displayName = '';

    if (!ObjectHelper.isUndefinedNullOrEmpty(siteNumber)) {
      displayName = siteNumber + ' ';
    }

    if (!ObjectHelper.isUndefinedNullOrEmpty(siteName)) {
      displayName += siteName + ' ';
    }

    if (!ObjectHelper.isUndefinedNullOrEmpty(siteCountry)) {
      displayName += '('+siteCountry+')';
    }

    return displayName;
  }

  listenToSiteChanges(): void {
    if (!this.parentForm)
      return;

    this.parentForm.get('siteId').valueChanges.subscribe((siteId: string) => {
      if (this.trialAutocomplete.selected) {
        this.patientService.getEngineSizesForTrialAndSite(this.trialAutocomplete.selected
          .id, siteId).subscribe(engineSizes => {
          this.engineSizes = [];
          engineSizes.forEach(engineSize => {
            this.engineSizes.push({ value: engineSize, text: engineSize });
          });
        });
      }

      if (this.suppressSiteChangeCheck) {
        this.suppressSiteChangeCheck = false;
        return;
      }

      if (this.changingSite && this.patientTrial && siteId && siteId !== this.patientTrial.siteId) {
        this.isLoadingChangeSitePreview = true;
        this.pendingSiteId = siteId;
        this.patientService.previewChangePatientTrialSite(this.patientTrial.id, siteId).subscribe({
          next: result => {
            this.isLoadingChangeSitePreview = false;
            if (result && result.impacts.length > 0) {
              this.changeSitePreview = result;
              this.changeSiteModal.show();
            }
            else {
              // With no impacts to show there is no dialog and so no confirm step to commit from.
              // Without this branch the change would be silently dropped (AB#34806).
              this.persistSiteChange();
            }
          },
          error: () => {
            this.isLoadingChangeSitePreview = false;
            this.pendingSiteId = null;
            this.pendingSiteName = null;
            this.alertService.showWarningAlert('Unable to check what this site change would affect, so it has not been applied. Select the site again to retry. If it keeps failing, contact IT support.');
          }
        });
      }
    });
  }

  onConfirmChangeSite() {
    this.changeSiteModal.hide();
    this.persistSiteChange();
  }

  /**
   * Commits the pending site change to the server.
   *
   * This has to be its own call because "Save Patient" cannot carry it: the update path runs through
   * PatientService.UpdatePatientTrial, which never assigns SiteId (UpdatePatientTrialDto.SiteId is
   * populated by the controller and then ignored). PUT patient/{patientTrialId}/site/{siteId} is the only
   * route that writes it, and before AB#34806 nothing in the app called it - so the user saw an impact
   * preview and the change was then discarded.
   *
   * Note this takes effect immediately and independently of Save Patient, which matches the endpoint's
   * design: it also moves eligible visits and remaps expense categories.
   */
  private persistSiteChange(): void {
    if (!this.pendingSiteId || !this.patientTrial) {
      return;
    }

    const siteId = this.pendingSiteId;
    const siteName = this.pendingSiteName;
    this.pendingSiteId = null;
    this.pendingSiteName = null;
    this.isLoadingChangeSitePreview = true;

    this.patientService.changePatientTrialSite(this.patientTrial.id, siteId).subscribe({
      next: () => {
        this.isLoadingChangeSitePreview = false;
        // Keep the local copy in step with what was just written, so the read-only field and any
        // subsequent Cancel both reflect the committed site rather than the one it replaced.
        this.patientTrial.siteId = siteId;
        if (siteName) {
          this.patientTrial.siteName = siteName;
        }
        this.changingSite = false;
        this.changeSitePreview = null;
        this.alertService.showSuccessAlert('Site Updated Successfully');
      },
      error: () => {
        this.isLoadingChangeSitePreview = false;
        // Put the form control back on the stored site so the dropdown cannot imply a change that did
        // not happen. changingSite deliberately stays true, leaving the user in the dropdown so they can
        // retry or cancel - which is also why selectedSiteLabel is not touched here: the read-only span
        // is hidden for as long as changingSite is set.
        this.suppressSiteChangeCheck = true;
        this.parentForm.patchValue({ siteId: this.patientTrial.siteId });
        this.alertService.showWarningAlert('Unable to change the site for this trial, so the patient is still at their previous site. Check the site is assigned to this trial, then select it again. If it keeps failing, contact IT support.');
      }
    });
  }

  onCancelChangeSite() {
    this.changeSiteModal.hide();
    this.changingSite = false;
    this.changeSitePreview = null;
    this.suppressSiteChangeCheck = true;
    this.parentForm.patchValue({ siteId: this.patientTrial.siteId });
    // Same null-site case as ngAfterViewInit: cancelling a site change on an enrolment that never had a
    // site must restore an empty label, not throw.
    this.selectedSiteLabel = this.truncateLabel(this.patientTrial.siteName);
  }

  /**
   * Truncates a label for the read-only display fields, tolerating a null/undefined/empty value.
   * Every caller previously inlined .substring(), which is why a null site or trial label crashed the
   * component instead of just rendering blank.
   */
  private truncateLabel(value: string, limit = 30): string {
    if (ObjectHelper.isUndefinedNullOrEmpty(value)) {
      return '';
    }

    return value.substring(0, limit) + (value.length > limit ? '...' : '');
  }

  private setEndingParticipationVisibility(stateOnTrial: string) {
    if (stateOnTrial === PatientStateOnTrialOptions.Dropout ||
      stateOnTrial === PatientStateOnTrialOptions.ScreenFail ||
      stateOnTrial === PatientStateOnTrialOptions.Withdrawn) {
      this.endingParticipationReasonVisible = true;
    }
    else {
      this.endingParticipationReasonVisible = false;
    }
  }

  onToggleOptions() {
    this.optionsVisible = !this.optionsVisible;
  }

  listenToTrialSelection(): void {
    this.trialAutocomplete.valueChanged.subscribe((option: TrialAutocomplete) => {
      this.parentForm.patchValue({ trialId: option.id });

      this.engineSizes = [];
      // We need to check that the selected trial is not already assigned to the patient
      this.parentForm.patchValue({ trialIdIsUnique: !this.assignedTrialIds.includes(option.id) });
    });
  }

  listenToPatientStateSelection(): void {
    this.stateOnTrialSelect.selectValueChanged.subscribe((option) => {
      this.setEndingParticipationVisibility(option);
    });
  }

  onTrialAdded() {
    const patientTrial = new PatientTrial({
      siteId: this.parentForm.get('siteId').value,
      trialId: this.parentForm.get('trialId').value,
      patientCode: this.parentForm.get('patientCode').value,
      intercashRemoteId: this.parentForm.get('intercashRemoteId').value,
      intercashSanNumber: this.parentForm.get('intercashSanNumber').value,
      trialCode: this.trialAutocomplete.selected.label,
      consent: this.parentForm.get('consent').value,
      bankFeesIncurred: this.parentForm.get('bankFeesIncurred').value,
      approvalNoReceipts: this.parentForm.get('approvalNoReceipts').value,
      overspendMileage: this.parentForm.get('overspendMileage').value,
      overspendTravel: this.parentForm.get('overspendTravel').value,
      fixedFeePerVisit: this.parentForm.get('fixedFeePerVisit').value,
      stateOnTrial: this.stateOnTrialSelect.selectedValue,
      endingParticipationReason: this.parentForm.get('endingParticipationReason').value,
      engineSize: this.engineSizeSelect.selectedValue,
    });

    this.trialAdded.emit(patientTrial);
    this.added = true;
  }

  onCancel() {
    this.added = false;
    this.cancelled.emit();
  }

  setFakeFieldValue(trial: TrialAutocomplete) {
    if (trial) {
      this.selectedTrialLabel = this.truncateLabel(trial.label);
    }
  }

  setFakeSiteFieldValue(site: SiteDropdownOption) {
    const base = site.trialSiteNumber ? `${site.trialSiteNumber} ${site.name}` : site.name;
    this.selectedSiteLabel = site.country ? `${base} ${site.country}` : base;
    // Held so that a committed change can update patientTrial.siteName. That field holds the bare site
    // name, not this composite display label, so the two are captured separately.
    this.pendingSiteName = site.name;
  }
}
